import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Camera, Loader2, Printer, RefreshCw, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../ui/button';
import API_BASE from '../../utils/apiBase';
import { blobToDataUrl, downloadStaffIdPrintSheet, fetchImageDataUrl } from '../../lib/staffIdPrint';
import StaffIdPhotoDialog from './StaffIdPhotoDialog';
import StaffWalletButtons from './StaffWalletButtons';

const API = API_BASE;

function statusClasses(key) {
  if (key === 'ready_for_work') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  if (key === 'onboarding') return 'bg-blue-100 text-blue-800 border-blue-200';
  if (key === 'unavailable') return 'bg-slate-100 text-slate-700 border-slate-200';
  return 'bg-amber-100 text-amber-800 border-amber-200';
}

export default function WorkerStaffIdCard() {
  const photoObjectUrlRef = useRef(null);
  const photoReadRef = useRef(0);
  const idReadRef = useRef(0);
  const [staffId, setStaffId] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoBlob, setPhotoBlob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [idError, setIdError] = useState(false);

  const token = localStorage.getItem('workerToken');

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  const clearObjectUrl = useCallback(() => {
    photoReadRef.current += 1;
    if (photoObjectUrlRef.current) {
      URL.revokeObjectURL(photoObjectUrlRef.current);
      photoObjectUrlRef.current = null;
    }
  }, []);

  const loadPhoto = useCallback(async (present) => {
    clearObjectUrl();
    const readId = photoReadRef.current;
    setPhotoUrl(null);
    setPhotoBlob(null);
    if (!present || !token) return;
    try {
      const response = await axios.get(`${API}/worker/staff-id/photo`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      if (readId !== photoReadRef.current) return;
      const blob = response.data;
      const objectUrl = URL.createObjectURL(blob);
      photoObjectUrlRef.current = objectUrl;
      setPhotoBlob(blob);
      setPhotoUrl(objectUrl);
    } catch (error) {
      console.warn('Could not load staff ID photo:', error);
    }
  }, [authHeaders, clearObjectUrl, token]);

  const loadId = useCallback(async () => {
    const readId = ++idReadRef.current;
    clearObjectUrl();
    setPhotoUrl(null);
    setPhotoBlob(null);
    setStaffId(null);
    setIdError(false);
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API}/worker/staff-id`, {
        headers: authHeaders,
      });
      const data = response.data || {};
      if (readId !== idReadRef.current) return;
      setStaffId(data);
      await loadPhoto(data.photo_present);
    } catch (error) {
      if (readId !== idReadRef.current) return;
      setStaffId(null);
      setIdError(true);
      clearObjectUrl();
      setPhotoUrl(null);
      setPhotoBlob(null);
      console.warn('Could not load staff ID:', error);
    } finally {
      if (readId === idReadRef.current) setLoading(false);
    }
  }, [authHeaders, clearObjectUrl, loadPhoto, token]);

  useEffect(() => {
    loadId();
    return () => {
      idReadRef.current += 1;
      clearObjectUrl();
    };
  }, [loadId, clearObjectUrl]);

  const handlePrint = async () => {
    if (!staffId?.issued) {
      toast.error('Your staff ID will be issued after recruitment approval.');
      return;
    }
    if (!photoBlob) {
      toast.error('Upload your staff ID photo first.');
      return;
    }
    setPrinting(true);
    try {
      const photoDataUrl = await blobToDataUrl(photoBlob);
      const rawLogoUrl = staffId?.branding?.logo_url || '/osabea_logo.png';
      const logoUrl = rawLogoUrl.startsWith('http')
        ? rawLogoUrl
        : `${window.location.origin}${rawLogoUrl.startsWith('/') ? rawLogoUrl : `/${rawLogoUrl}`}`;
      const logoDataUrl = await fetchImageDataUrl(logoUrl);
      await downloadStaffIdPrintSheet({ staffId, photoDataUrl, logoDataUrl });
    } catch (error) {
      toast.error(error.message || 'Could not create the staff ID print sheet');
    } finally {
      setPrinting(false);
    }
  };

  if (loading && !staffId) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="worker-staff-id-loading">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading your staff ID…
        </div>
      </div>
    );
  }

  if (!staffId) return idError ? (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" role="alert">
      <p className="text-sm text-slate-700">We could not refresh your staff ID. Please try again.</p>
      <Button type="button" variant="outline" className="mt-3" onClick={loadId}>Try again</Button>
    </div>
  ) : null;

  const brand = staffId.branding || {};

  return (
    <section
      className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
      data-testid="worker-staff-id-card"
    >
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">My staff ID</p>
          <p className="mt-0.5 text-sm text-slate-600">The same identity is used for digital, Wallet and printed cards.</p>
        </div>
        <button
          type="button"
          onClick={loadId}
          className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
          title="Refresh staff ID"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      <div className="grid gap-4 p-4 md:grid-cols-[minmax(0,320px)_1fr]">
        <div
          className="mx-auto w-full max-w-[300px] overflow-hidden rounded-[22px] border-2 bg-white shadow-md"
          style={{ borderColor: brand.primary_color || '#0F5C5E' }}
        >
          <div
            className="px-4 py-4 text-white"
            style={{ backgroundColor: brand.primary_color || '#0F5C5E' }}
          >
            <div className="flex items-center gap-3">
              <img
                src={brand.logo_url || '/osabea_logo.png'}
                alt=""
                className="h-10 w-10 rounded-lg bg-white/95 object-contain p-1"
                onError={(event) => { event.currentTarget.style.display = 'none'; }}
              />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">{brand.organisation_name || 'Osabea Healthcare Solutions Ltd'}</p>
                <p className="text-[11px] font-semibold tracking-[0.16em] text-white/80">AGENCY STAFF</p>
              </div>
            </div>
          </div>

          <div className="p-4 text-center">
            <div className="relative mx-auto h-36 w-28 overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
              {photoUrl ? (
                <img src={photoUrl} alt="Staff ID" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full items-center justify-center px-3 text-xs text-slate-500">
                  Photo required
                </div>
              )}
            </div>
            <p className="mt-3 text-lg font-bold text-slate-900">{staffId.display_name || 'Staff member'}</p>
            <p className="text-sm text-slate-600">{staffId.role || 'Agency Staff'}</p>
            <p className="mt-2 font-mono text-sm font-semibold" style={{ color: brand.primary_color || '#0F5C5E' }}>
              {staffId.staff_id || 'ID pending'}
            </p>
            <div className={`mx-auto mt-3 inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${statusClasses(staffId?.work_status?.key)}`}>
              {staffId?.work_status?.label || 'Status pending'}
            </div>
          </div>
        </div>

        <div className="flex flex-col justify-center gap-3">
          {(() => {
            const prompt = staffId.photo_prompt || {};
            const priority = prompt.priority || (staffId.photo_present ? 'none' : 'optional');
            if (staffId.photo_present) {
              return (
                <div
                  className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800"
                  data-testid="staff-id-photo-saved"
                >
                  <ShieldCheck className="h-4 w-4 shrink-0" />
                  Staff ID photo saved
                </div>
              );
            }
            if (priority === 'action') {
              return (
                <div
                  className="rounded-xl border-2 p-3"
                  style={{ borderColor: brand.primary_color || '#0F5C5E', backgroundColor: 'rgba(15,92,94,0.04)' }}
                  data-testid="staff-id-photo-prompt-action"
                >
                  <p className="text-sm font-semibold text-slate-900">{prompt.headline || 'Add your staff ID photo'}</p>
                  <p className="mt-1 text-xs text-slate-600">
                    {prompt.subhead || 'This photo appears on your staff ID card. It only takes a moment.'}
                  </p>
                </div>
              );
            }
            return (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="staff-id-photo-prompt-optional">
                <p className="text-sm font-semibold text-amber-900">{prompt.headline || 'Add your staff ID photo'}</p>
                <p className="mt-1 text-xs text-amber-800">
                  {prompt.subhead || 'You can add the photo for your staff ID now, or later during onboarding.'}
                </p>
              </div>
            );
          })()}

          <Button
            type="button"
            variant={staffId.photo_present ? 'outline' : 'default'}
            className="h-12 justify-center gap-2 rounded-xl text-base"
            style={staffId.photo_present ? undefined : { backgroundColor: brand.primary_color || '#0F5C5E' }}
            onClick={() => setPhotoDialogOpen(true)}
            data-testid="staff-id-open-photo-journey"
          >
            <Camera className="h-5 w-5" />
            {staffId.photo_present ? 'Replace ID photo' : 'Add your staff ID photo'}
          </Button>

          {staffId.issued ? (
            <Button
              type="button"
              variant="outline"
              className="justify-start gap-2 rounded-xl"
              onClick={handlePrint}
              disabled={!staffId.photo_present || printing}
            >
              {printing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
              Print / laminate this ID
            </Button>
          ) : (
            <p className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
              Your photo can be added now. Your permanent staff number and QR ID are issued automatically after recruitment approval.
            </p>
          )}

          <StaffWalletButtons wallet={staffId.wallet} authHeaders={authHeaders} />
        </div>
      </div>

      <StaffIdPhotoDialog
        open={photoDialogOpen}
        onOpenChange={setPhotoDialogOpen}
        apiBase={API}
        authHeaders={authHeaders}
        uploadPath="/worker/staff-id/photo"
        prompt={staffId.photo_prompt}
        hasExistingPhoto={Boolean(staffId.photo_present)}
        brandColor={brand.primary_color || '#0F5C5E'}
        onUploaded={loadId}
      />
    </section>
  );
}
